import { config } from "@config/config";
import { useQueryClient } from "@tanstack/react-query";
import { isNewSyncStamp, manualSyncStartFailure } from "@utils/ehrActions";
import { isSyncDone } from "@utils/learningTreeReview";
import { useCallback, useEffect, useRef } from "react";

import {
  ApiLearnerManualGoalSyncStatusChoices,
  StartManualSyncWorkflowMutationVariables,
  useLearnerManualGoalSyncByIdQuery,
  useStartManualSyncWorkflowMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useEHRConnection } from "@providers/EHRConnectionProvider";

const ACTIVE_SYNC_STATUSES = new Set([
  ApiLearnerManualGoalSyncStatusChoices.Inprogress,
  ApiLearnerManualGoalSyncStatusChoices.Pending,
]);

interface UseLearnerProgrammingRefreshOptions {
  learnerId: string;
  /** Extra StartManualSyncWorkflow args; null while the caller can't start a refresh yet (e.g. no report loaded). */
  startVariables: Omit<StartManualSyncWorkflowMutationVariables, "learnerId"> | null;
  /** Runs once per newly completed sync, to refresh whatever the caller shows. */
  onSyncCompleted: () => void;
}

/** Refresh a learner's programming from their EHR (manual sync) and poll it to completion. */
export const useLearnerProgrammingRefresh = ({
  learnerId,
  startVariables,
  onSyncCompleted,
}: UseLearnerProgrammingRefreshOptions) => {
  const { isOrgUsernamePassword, ehrConnectionStatus, orgEhrUnusable, noCredentials, isEhrSyncCleared } =
    useEHRConnection();

  const queryClient = useQueryClient();
  // The server's lastUpdated when this hook first saw the learner's sync status,
  // then the stamp of the last Completed it acted on. onSyncCompleted only runs
  // for a Completed with a different stamp, so a Completed left from an earlier
  // run, including one the next refresh's start answers with, never triggers a
  // false refresh.
  const syncStampBaselineRef = useRef<string | null | undefined>(undefined);

  const {
    data,
    refetch: refetchStatus,
    isPending: isManualSyncStatusPending,
  } = useLearnerManualGoalSyncByIdQuery(
    { learnerId },
    {
      enabled: !!learnerId,
      refetchInterval: (query) => {
        const status = query.state?.data?.learnerById?.manualGoalSyncStatus;
        if (ACTIVE_SYNC_STATUSES.has(status as ApiLearnerManualGoalSyncStatusChoices)) {
          return config.REPORT_POLLING_INTERVAL;
        }
        return false;
      },
    },
  );
  // Tracks whether a sync was started from this hook in this session, so the
  // failure effect below only reacts to an attempt the user just made — not a
  // leftover Failure status already present on mount.
  const syncStartedThisSessionRef = useRef<boolean>(false);

  const { mutate: startManualSyncWorkflowMutation, isPending: isStartManualSyncWorkflowPending } =
    useStartManualSyncWorkflowMutation({
      onSuccess: async (data) => {
        const startFailure = manualSyncStartFailure(data.startManualSyncWorkflow);
        if (startFailure) {
          // Most refusals are about the EHR connection (session expired, no
          // credentials, org session not ready), so refresh what the badge shows.
          // Some also write a fresh Failure to the learner, so read it once.
          syncStartedThisSessionRef.current = false;
          notifyError(startFailure);
          void queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
          void queryClient.invalidateQueries({ queryKey: ["getHasEhrCredentials"] });
          await refetchStatus();
          return;
        }
        notifySuccess("Import started successfully");
        await refetchStatus();
      },
      onError: () => {
        notifyError("Failed to start import");
      },
    });

  const hasSyncStatus = data !== undefined;
  const syncStatus = data?.learnerById?.manualGoalSyncStatus;
  const lastUpdated = data?.learnerById?.manualGoalSyncLastUpdated;

  const isSyncing =
    isManualSyncStatusPending ||
    isStartManualSyncWorkflowPending ||
    ACTIVE_SYNC_STATUSES.has(syncStatus as ApiLearnerManualGoalSyncStatusChoices);

  const canRefresh = !!learnerId && startVariables !== null;

  const refresh = useCallback(() => {
    // Managed-onboarding gate: an uncleared tenant can't trigger an EHR re-sync
    // (the backend rejects it too). Guarded here so no call site can bypass it.
    if (!canRefresh || isSyncing || orgEhrUnusable || !isEhrSyncCleared) return;
    syncStartedThisSessionRef.current = true;
    startManualSyncWorkflowMutation({ learnerId, ...startVariables });
  }, [
    canRefresh,
    learnerId,
    startVariables,
    isSyncing,
    orgEhrUnusable,
    isEhrSyncCleared,
    startManualSyncWorkflowMutation,
  ]);

  useEffect(() => {
    if (!hasSyncStatus) return;
    if (syncStampBaselineRef.current === undefined) {
      syncStampBaselineRef.current = lastUpdated ?? null;
      return;
    }
    if (isSyncDone(syncStatus) && isNewSyncStamp(lastUpdated, syncStampBaselineRef.current)) {
      syncStampBaselineRef.current = lastUpdated;
      onSyncCompleted();
    }
  }, [hasSyncStatus, syncStatus, lastUpdated, onSyncCompleted]);

  // Org-level admin account: this flow polls its own sync status, not
  // ehr_connection, so the badge query never starts polling on its own. When a
  // sync started in this session resolves to Failure, refetch the connection so a
  // credential failure surfaces on the top badge without a manual reload —
  // matching ResyncGoals and the reassessment auto-start flow.
  useEffect(() => {
    if (!syncStartedThisSessionRef.current) return;
    if (syncStatus === ApiLearnerManualGoalSyncStatusChoices.Failure) {
      syncStartedThisSessionRef.current = false;
      if (isOrgUsernamePassword) {
        void queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      }
    } else if (isSyncDone(syncStatus)) {
      syncStartedThisSessionRef.current = false;
    }
  }, [syncStatus, isOrgUsernamePassword, queryClient]);

  return {
    refresh,
    isSyncing,
    lastUpdated,
    syncStatus,
    canRefresh,
    orgEhrUnusable,
    ehrConnectionStatus,
    noCredentials,
    isEhrSyncCleared,
  };
};

export type LearnerProgrammingRefreshState = ReturnType<typeof useLearnerProgrammingRefresh>;
